/**
 * Internal document helpers shared by the wf-patch apply/diff/invert engines.
 *
 * All mutation helpers preserve the canonical `elementOrder` invariant declared
 * in wf-types.ts: buckets exist only while non-empty, and an element id appears
 * in at most one bucket — the one keyed by its `containerId`.
 */

import { isJsonObject } from "../sd-patch/sd-json";
import { IWfDocument, IWfElement } from "./wf-types";

/** Where an element sits inside its container's order bucket. */
export interface IWfOrderAnchor {
  containerId: string;
  /** null = it was first; string = the preceding sibling id; undefined = not listed (append). */
  afterSiblingId: string | null | undefined;
}

export function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value !== "";
}

/** Runtime-defensive containerId read: "" when the field is malformed. */
export function containerIdOf(element: IWfElement): string {
  return typeof element.containerId === "string" ? element.containerId : "";
}

/** Ensures the doc's `elements` / `elementOrder` records exist (mirrors sd-apply's root ensure). */
export function ensureDocShape(doc: IWfDocument): void {
  if (!isJsonObject(doc.elements)) {
    doc.elements = {};
  }
  if (!isJsonObject(doc.elementOrder)) {
    doc.elementOrder = {};
  }
}

/** Removes `elementId` from every order bucket, deleting buckets it empties. */
export function removeFromAllOrderBuckets(doc: IWfDocument, elementId: string): void {
  for (const bucketKey of Object.keys(doc.elementOrder)) {
    const bucket: string[] = doc.elementOrder[bucketKey];
    if (!Array.isArray(bucket)) {
      continue;
    }
    const index: number = bucket.indexOf(elementId);
    if (index < 0) {
      continue;
    }
    bucket.splice(index, 1);
    if (bucket.length === 0) {
      delete doc.elementOrder[bucketKey];
    }
  }
}

export function appendToOrderBucket(doc: IWfDocument, containerId: string, elementId: string): void {
  const bucket: string[] | undefined = doc.elementOrder[containerId];
  if (Array.isArray(bucket)) {
    if (!bucket.includes(elementId)) {
      bucket.push(elementId);
    }
    return;
  }
  doc.elementOrder[containerId] = [elementId];
}

/**
 * Inserts `elementId` into the container's bucket. The caller must have
 * detached the id first (detach-first mirrors sd-apply, so same-container
 * reorders resolve the anchor against the post-detach array).
 * null anchor = first; a missing anchor appends; absent anchor appends.
 */
export function insertIntoOrderBucket(
  doc: IWfDocument,
  containerId: string,
  elementId: string,
  afterSiblingId: string | null | undefined
): void {
  let bucket: string[] | undefined = doc.elementOrder[containerId];
  if (!Array.isArray(bucket)) {
    bucket = [];
    doc.elementOrder[containerId] = bucket;
  }
  let insertionIndex: number = bucket.length;
  if (afterSiblingId === null) {
    insertionIndex = 0;
  } else if (typeof afterSiblingId === "string") {
    const anchorIndex: number = bucket.indexOf(afterSiblingId);
    if (anchorIndex >= 0) {
      insertionIndex = anchorIndex + 1;
    }
  }
  bucket.splice(insertionIndex, 0, elementId);
}

/**
 * Whether re-containing `elementId` under `containerId` would create a
 * containment cycle: the chain from `containerId` up to the root passes
 * through (or is) the element itself. A visited-set guards against walking
 * pre-existing malformed cycles forever.
 */
export function containerChainReaches(doc: IWfDocument, containerId: string, elementId: string): boolean {
  const visited: Set<string> = new Set<string>();
  let current: string = containerId;
  while (current !== "") {
    if (current === elementId) {
      return true;
    }
    if (visited.has(current)) {
      return false;
    }
    visited.add(current);
    const container: IWfElement | undefined = doc.elements[current];
    if (container === undefined) {
      return false;
    }
    current = containerIdOf(container);
  }
  return false;
}

/** Containment depth of an element ("" chain length); malformed cycles terminate. */
export function containerChainDepth(doc: IWfDocument, elementId: string): number {
  const visited: Set<string> = new Set<string>();
  let depth: number = 0;
  const element: IWfElement | undefined = doc.elements[elementId];
  let current: string = element === undefined ? "" : containerIdOf(element);
  while (current !== "" && !visited.has(current)) {
    visited.add(current);
    depth++;
    const container: IWfElement | undefined = doc.elements[current];
    current = container === undefined ? "" : containerIdOf(container);
  }
  return depth;
}

/** The element's current order slot, expressed anchor-relative (never by index). */
export function orderAnchorOf(doc: IWfDocument, elementId: string): IWfOrderAnchor {
  const element: IWfElement | undefined = doc.elements[elementId];
  const containerId: string = element === undefined ? "" : containerIdOf(element);
  const bucket: string[] | undefined = doc.elementOrder[containerId];
  if (!Array.isArray(bucket)) {
    return { containerId, afterSiblingId: undefined };
  }
  const index: number = bucket.indexOf(elementId);
  if (index < 0) {
    return { containerId, afterSiblingId: undefined };
  }
  if (index === 0) {
    return { containerId, afterSiblingId: null };
  }
  return { containerId, afterSiblingId: bucket[index - 1] };
}

/** Index of the element inside its container's bucket; large when unlisted. */
export function orderIndexOf(doc: IWfDocument, elementId: string): number {
  const element: IWfElement | undefined = doc.elements[elementId];
  const containerId: string = element === undefined ? "" : containerIdOf(element);
  const bucket: string[] | undefined = doc.elementOrder[containerId];
  if (!Array.isArray(bucket)) {
    return Number.MAX_SAFE_INTEGER;
  }
  const index: number = bucket.indexOf(elementId);
  return index < 0 ? Number.MAX_SAFE_INTEGER : index;
}
